import i18n from '../../../i18n';
import { parseAuthSessionPayload } from '../../../shared/services/authRefresh';
import { ClientAuthSession, getBootstrapTenantId, getRefreshToken } from '../../../shared/services/authSession';
import { getPathTenantSlug, pathTenantHeaders } from '../../../shared/config/workspaceHost';

export interface LoginRequest {
  tenantId?: string;
  username: string;
  password: string;
}

export interface LoginResult extends ClientAuthSession {}

export class PasswordChangeRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PasswordChangeRequiredError';
  }
}

type ApiErrorDetail = {
  field?: string;
  issue?: string;
  Field?: string;
  Issue?: string;
};

type ApiErrorPayload = {
  code?: string;
  Code?: string;
  message?: string;
  Message?: string;
  details?: ApiErrorDetail[];
  Details?: ApiErrorDetail[];
};

type ApiEnvelopePayload = {
  error?: ApiErrorPayload;
  Error?: ApiErrorPayload;
  code?: string;
  Code?: string;
  message?: string;
  Message?: string;
  details?: ApiErrorDetail[];
  Details?: ApiErrorDetail[];
};

const readErrorDetail = (detail: ApiErrorDetail): { field?: string; issue?: string } => ({
  field: detail.field ?? detail.Field,
  issue: detail.issue ?? detail.Issue,
});

export interface ForgotPasswordResult {
  message: string;
  resetToken?: string | null;
  expiresAt?: string | null;
}

interface TenantRequestContext {
  tenantId?: string;
  tenantUrl?: string;
}

interface FirstLoginPasswordChangeRequest {
  username: string;
  currentPassword: string;
  newPassword: string;
  tenantId?: string;
  tenantUrl?: string;
}

export interface ResetPasswordResult {
  message: string;
}

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

type TenantRequestInput = TenantRequestContext | string | undefined;

const readStoredTenantId = (): string | undefined => getBootstrapTenantId() || undefined;

const resolveTenantRequestContext = (explicitTenant?: TenantRequestInput): TenantRequestContext => {
  const explicitTenantContext = typeof explicitTenant === 'string'
    ? { tenantId: explicitTenant }
    : (explicitTenant || {});
  const storedTenantId = readStoredTenantId();
  const tenantId = (explicitTenantContext.tenantId || storedTenantId || '').trim();
  const pathSlug = getPathTenantSlug();
  const tenantUrl = (explicitTenantContext.tenantUrl
    || (typeof window !== 'undefined' && !pathSlug ? window.location.origin : '')
    || '').trim();

  return {
    tenantId,
    tenantUrl,
  };
};

const buildJsonHeaders = (explicitTenant?: TenantRequestInput): Record<string, string> => {
  const { tenantId, tenantUrl } = resolveTenantRequestContext(explicitTenant);

  return {
    'Content-Type': 'application/json',
    ...(tenantId ? { TenantId: tenantId, 'X-Tenant-Id': tenantId } : {}),
    ...(tenantUrl ? { 'X-Tenant-Url': tenantUrl } : {}),
    ...pathTenantHeaders(),
  };
};

const resolveApiError = (payload: unknown): ApiErrorPayload => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const candidate = payload as ApiEnvelopePayload;
  const nested = candidate.error ?? candidate.Error;
  if (nested) {
    return {
      code: nested.code ?? nested.Code,
      message: nested.message ?? nested.Message,
      details: (nested.details ?? nested.Details)?.map(readErrorDetail),
    };
  }

  return {
    code: candidate.code ?? candidate.Code,
    message: candidate.message ?? candidate.Message,
    details: (candidate.details ?? candidate.Details)?.map(readErrorDetail),
  };
};

const getLoginFailureMessage = (error: ApiErrorPayload): string => {
  if (error.code === 'SSO_REQUIRED') {
    return i18n.t('auth:errors.ssoRequired');
  }

  if (error.code === 'UNAUTHORIZED') {
    return i18n.t('auth:errors.invalidCredentialsOrTenant');
  }

  if (error.code === 'VALIDATION_ERROR') {
    const validationIssue = error.details
      ?.map(readErrorDetail)
      .find(detail => detail.issue?.trim())?.issue;
    if (validationIssue) {
      return validationIssue;
    }
  }

  return error.message || i18n.t('auth:errors.invalidCredentialsOrTenant');
};

export interface SsoStatus {
  configured: boolean;
  enabled: boolean;
  protocol?: string | null;
}

export const authService = {
  getSsoStatus: async (): Promise<SsoStatus> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/sso/status`, {
      method: 'GET',
      headers: buildJsonHeaders(),
    });
    if (!response.ok) {
      return { configured: false, enabled: false };
    }
    return response.json();
  },

  startSso: async (email: string, returnUrl?: string): Promise<string> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/sso/start`, {
      method: 'POST',
      headers: buildJsonHeaders(),
      body: JSON.stringify({ email, returnUrl }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(resolveApiError(payload).message || i18n.t('auth:errors.unableToLogin'));
    }
    const redirectUrl = payload?.redirectUrl || payload?.RedirectUrl;
    if (!redirectUrl) {
      throw new Error(i18n.t('auth:errors.unableToLogin'));
    }
    return redirectUrl;
  },

  completeSso: async (code: string): Promise<LoginResult> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/sso/complete`, {
      method: 'POST',
      headers: buildJsonHeaders(),
      body: JSON.stringify({ code }),
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(resolveApiError(payload).message || i18n.t('auth:errors.unableToLogin'));
    }
    return parseAuthSessionPayload(payload);
  },

  login: async (request: LoginRequest): Promise<LoginResult> => {
    const pathSlug = getPathTenantSlug();
    const tenantId = (request.tenantId || (!pathSlug ? window.location.origin : '') || '').trim();
    const requestBody = {
      ...(tenantId ? { tenantId } : {}),
      username: request.username,
      password: request.password,
    };

    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: buildJsonHeaders(tenantId || undefined),
        body: JSON.stringify(requestBody),
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Network error';
      if (/failed to fetch|networkerror|err_connection_refused/i.test(message)) {
        const configuredBaseUrl = API_BASE_URL || '(missing REACT_APP_API_BASE_URL)';
        throw new Error(`${i18n.t('auth:errors.cannotConnectBackend')} [${configuredBaseUrl}]`);
      }
      throw error;
    }

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const error = resolveApiError(payload);
      const code = error.code;
      const message = getLoginFailureMessage(error);

      if (code === 'PASSWORD_CHANGE_REQUIRED') {
        throw new PasswordChangeRequiredError(message);
      }

      throw new Error(message);
    }

    return parseAuthSessionPayload(payload);
  },

  refresh: async (tenant?: TenantRequestInput): Promise<LoginResult> => {
    const refreshToken = getRefreshToken();
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: buildJsonHeaders(tenant),
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const error = resolveApiError(payload);
      throw new Error(error.message || i18n.t('auth:errors.sessionExpired'));
    }

    return parseAuthSessionPayload(payload, resolveTenantRequestContext(tenant).tenantId);
  },

  forgotPassword: async (username: string, tenant?: TenantRequestInput): Promise<ForgotPasswordResult> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password`, {
      method: 'POST',
      headers: buildJsonHeaders(tenant),
      body: JSON.stringify({ username }),
    });

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const message = payload?.error?.message || payload?.message || i18n.t('auth:errors.requestResetFailed');
      throw new Error(message);
    }

    return {
      message: payload?.message || i18n.t('auth:messages.requestResetFallback'),
      resetToken: payload?.resetToken ?? null,
      expiresAt: payload?.expiresAt ?? null,
    };
  },

  resetPassword: async (
    resetToken: string,
    newPassword: string,
    tenant?: TenantRequestInput,
    username?: string,
  ): Promise<ResetPasswordResult> => {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/reset-password`, {
      method: 'POST',
      headers: buildJsonHeaders(tenant),
      body: JSON.stringify({
        resetToken,
        newPassword,
        ...(username?.trim() ? { username: username.trim() } : {}),
      }),
    });

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const message = payload?.error?.message || payload?.message || i18n.t('auth:errors.resetPasswordFailed');
      throw new Error(message);
    }

    return {
      message: payload?.message || i18n.t('auth:messages.resetPasswordSuccess'),
    };
  },

  firstLoginPasswordChange: async (request: FirstLoginPasswordChangeRequest): Promise<ResetPasswordResult> => {
    const { tenantId, tenantUrl, ...requestBody } = request;
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/first-login/password`, {
      method: 'POST',
      headers: buildJsonHeaders({ tenantId, tenantUrl }),
      body: JSON.stringify(requestBody),
    });

    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const message = payload?.error?.message || payload?.message || i18n.t('auth:errors.changePasswordFailed');
      throw new Error(message);
    }

    return {
      message: payload?.message || i18n.t('auth:messages.resetPasswordSuccess'),
    };
  },
};

export default authService;
